import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {useSelector} from 'react-redux';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import './index.scss';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {GatewayModel} from '../../../../types/gateway';
import {useMapSelector} from '../../../../hooks/map-selector';
import {BridgeDeviceTypeModel} from '../../../../types/bridge-device-type';
import {useMessage} from '../../../../hooks/message';
import {isValidRFAddress} from '../../../../utils/validation';
import {useRPC} from '../../../../hooks/rpc';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';

const BACK_URL = '/settings/device';

export const DeviceEditScreen = memo(function DeviceEditScreen() {
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard] = useKeyboard('numpad', true);
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const alarmConfig = useSelector(state => state.control.config.alarm);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const GATEWAY_OPTION_ITEM_LIST = useMapSelector(
        state => state.control.gatewayList,
        gatewayModel => ({value: gatewayModel, label: gatewayModel.name}),
        [],
    );
    const DEVICE_TYPE_SELECTION_ITEMS = useMapSelector(
        state => state.control.bridgeDeviceTypeList,
        device => ({value: device, label: device.name}),
        [],
    );

    const [selected, setSelected] = useState<BridgeDeviceModel>(null);
    const [name, setName] = useState('');
    const [type, setType] = useState<SelectItem<BridgeDeviceTypeModel>>(null);
    const [rfAddress, setRFAddress] = useState('');
    const [gateway, setGateway] = useState<SelectItem<GatewayModel>>(null);

    const nextFreeRfId = useMemo(() => {
        const usedRfAddressList = bridgeDeviceList.filter(v => v.gateway_id === gateway?.value?.id).map(value => value.rf_address);
        for (let i = 10; i < 255; i++) {
            if (!usedRfAddressList.includes(i)) {
                return i.toString();
            }
        }

        return '';
    }, [bridgeDeviceList, gateway?.value?.id]);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteBridgeDevice(selected.id)
            .then(() => {
                success('Cihaz başarılı bir şekilde silindi.');
                onBackPressed();
            })
            .catch(failure);
    }, [failure, onBackPressed, rpc, selected, success]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure('Lütfen geçerli bir cihaz adı giriniz.');

            return false;
        }

        if (type == null || type.value == null) {
            failure('Lütfen geçerli bir cihaz tipi seçiniz.');

            return false;
        }

        if (!isValidRFAddress(rfAddress)) {
            failure('Lütfen geçerli RF adresi giriniz.');

            return false;
        }

        if (gateway == null || gateway.value == null) {
            failure('Lütfen geçerli bir gateway seçiniz.');

            return false;
        }

        return true;
    }, [failure, gateway, name, rfAddress, type]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const bridgeDeviceModel: BridgeDeviceModel = {
            ...selected,
            id: selected != null ? selected.id : null,
            type_id: type.value.id,
            name,
            rf_address: Number(rfAddress),
            gateway_id: gateway.value.id,
            temperature_offset: selected != null ? selected.temperature_offset : 0,
            temperature_hysteresis: selected != null ? selected.temperature_hysteresis : 2,
            alarm_config: selected != null ? selected.alarm_config : alarmConfig,
        };

        return rpc.updateBridgeDevice(bridgeDeviceModel)
            .then(() => {
                success('Cihaz başarılı bir şekilde ' + (bridgeDeviceModel.id ? 'düzenlendi.' : 'oluşturuldu.'));
                onBackPressed();
            })
            .catch(failure);
    }, [
        isFieldsValid,
        name,
        type,
        rfAddress,
        gateway,
        alarmConfig,
        selected,
        rpc,
        success,
        failure,
        onBackPressed,
    ]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type="bridgeDevice"
                    onPress={onDeletePressed}
                    questionText="Silmek istediğinizden emin misiniz?"
                    questionTextRelational={message => `${selected?.name} ile beraber ${message} silinecektir. Devam edilsin mi?`}/>,
        },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            url: BACK_URL,
        },
    ], [onCreatePressed, onDeletePressed, selected]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setType(DEVICE_TYPE_SELECTION_ITEMS[0]);
        setGateway(GATEWAY_OPTION_ITEM_LIST[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [GATEWAY_OPTION_ITEM_LIST]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const device = bridgeDeviceList.find(device => device.id === params['id']);
        if (device == null) {
            return;
        }

        setSelected(device);
        setName(device.name);
        setType(DEVICE_TYPE_SELECTION_ITEMS.find(option => option.value.id === device.type_id));
        setGateway(GATEWAY_OPTION_ITEM_LIST.find(gatewayOptionItem => gatewayOptionItem.value.id === device.gateway_id));
        setRFAddress(device.rf_address.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id == null) {
            setRFAddress(nextFreeRfId);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextFreeRfId]);

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer className="service-edit" buttons={buttons} backUrl={BACK_URL}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder="Cihaz adı yazınız..."
                            label="Cihaz Adı"
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label="Cihaz Tipi"
                            options={DEVICE_TYPE_SELECTION_ITEMS}
                            onChange={setType}
                            value={type}/>
                        <SettingInputComponent
                            placeholder="RF adresi yazınız..."
                            label="RF adresi"
                            value={rfAddress}
                            setValue={setRFAddress}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label="Gateway Adı"
                            options={GATEWAY_OPTION_ITEM_LIST}
                            onChange={setGateway}
                            value={gateway}/>
                    </div>
                    <div className="content-wrapper">

                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
